import { useAuth } from '@/hooks'
import { CategoriaEnum } from '@acme/auth'
import { filterLocaisCentro } from '@acme/common'
import {
    Box,
    Checkbox,
    CircularProgress,
    InputAdornment,
    ListItemText,
    SelectChangeEvent,
    TextField,
} from '@mui/material'
import dayjs from 'dayjs'
import { FormField } from 'global/components/Form'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { funcionario, Funcionario } from 'service/api'
import { useStore } from 'store'
import Menu from '../MenuV2'
import { SelectProps } from './types'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const DEBOUNCE_MS = 300

type Props = Omit<
    SelectProps,
    'buttom' | 'isSupervisor' | 'form' | 'defaultValue'
> & {
    checked?: number[]
    onChange?: (ids: number[]) => void
    defaultValues?: number[]
    /** Utiliza filtro de locais e centro de custo */
    useFilter?: boolean
    value?: number[]
    form?: boolean | string
    /** Chamado quando o estado de "Todos" muda */
    onSelectAllChange?: (isAll: boolean) => void
}

export const SelectFuncionarios: React.FC<Props> = ({
    size = 'small',
    form,
    children,
    disabled,
    checked,
    onChange,
    defaultValues = [],
    useFilter = false,
    value,
    width = 260,
    onSelectAllChange,
    ...props
}) => {
    const formName = typeof form === 'string' ? form : 'funcionarioIds'
    const {
        decoded: { categoria },
    } = useAuth()
    const formContext = useFormContext()
    const empresaId = useStore(state => state.companyId)
    const centroCustoId = useStore(state => state.costCenterId)
    const locais = useStore(state => state.locaisIds)
    const [competencia, competenciaAtual] = useStore(state => [
        state.competencia,
        state.competenciaAtual,
    ])
    const diasRescisao = useStore(state => state.company?.diasRescisao)

    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [selectAll, setSelectAll] = useState(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout>>()

    const handleSearchChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value
            setSearch(value)
            if (debounceRef.current) clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
                setDebouncedSearch(value)
            }, DEBOUNCE_MS)
        },
        [],
    )

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [])

    const queryParams: Record<string, unknown> = { empresaId }
    if (useFilter && centroCustoId > 0)
        queryParams.centroCustoId = centroCustoId
    if (useFilter && locais.length) queryParams.localId = locais[0]
    if (debouncedSearch) queryParams.search = debouncedSearch

    const {
        data: pages,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = funcionario.useInfiniteScroll({
        queryParams,
        enabled: empresaId > 0,
    })

    const allLoaded = useMemo(
        () => pages?.pages.flatMap(p => p.data) ?? [],
        [pages],
    ) as Funcionario[]

    const exibirApenasAtivos = categoria !== CategoriaEnum.Funcionario
    const filtrarFuncionario = useCallback(
        (obj: Funcionario) => {
            if (exibirApenasAtivos && obj.dtRescisao) {
                const dataRef =
                        competencia === competenciaAtual
                            ? dayjs()
                            : dayjs(competencia).startOf('month'),
                    tolerancia = diasRescisao ?? 40

                if (
                    dataRef
                        .add(tolerancia, 'day')
                        .isAfter(dayjs(obj.dtRescisao), 'day')
                )
                    return false
            }

            if (useFilter === false) return true
            return filterLocaisCentro<typeof obj>(
                locais,
                [centroCustoId!],
                it => it as any,
            )(obj)
        },
        [
            exibirApenasAtivos,
            centroCustoId,
            competencia,
            competenciaAtual,
            diasRescisao,
            locais,
            useFilter,
        ],
    )
    const data = useMemo(
        () => allLoaded.filter(filtrarFuncionario),
        [allLoaded, filtrarFuncionario],
    )

    const [_employeeIds, _setEmployeeIds] = useState<number[]>(defaultValues)
    const employeeIds = value ? value : _employeeIds
    const setEmployeeIds = (ids: number[]) => {
        _setEmployeeIds(ids)
        onChange?.(ids)
    }

    useEffect(() => {
        if (useFilter && (centroCustoId > 0 || locais.length)) {
            setEmployeeIds([])
            setSelectAll(false)
            onSelectAllChange?.(false)
        }
    }, [centroCustoId, locais, useFilter])

    // "Todos" precisa enviar os ids reais dos funcionários — o backend não
    // expande seleção vazia. Busca a lista completa (sem paginação) apenas
    // quando o usuário marca "Todos" e resolve os ids filtrados.
    const { data: todosFuncionarios = [] } = funcionario.useFetch<
        Funcionario[]
    >({
        queryParams: {
            empresaId,
            ...(useFilter && centroCustoId > 0 ? { centroCustoId } : {}),
            ...(useFilter && locais.length ? { localId: locais[0] } : {}),
        } as any,
        enabled: selectAll && empresaId > 0,
    })

    useEffect(() => {
        if (!selectAll) return
        const ids = todosFuncionarios
            .filter(filtrarFuncionario)
            .map(({ id }) => id)
            .filter((id): id is number => id != null && !checked?.includes(id))
        setEmployeeIds(ids)
    }, [selectAll, todosFuncionarios, filtrarFuncionario])

    const handleChange = (event: SelectChangeEvent<unknown>) => {
        const {
            target: { value },
        } = event

        if ((value as any[]).includes('todos')) {
            if (selectAll) {
                setSelectAll(false)
                setEmployeeIds([])
                onSelectAllChange?.(false)
            } else {
                setSelectAll(true)
                setEmployeeIds([])
                onSelectAllChange?.(true)
            }
        } else {
            setSelectAll(false)
            setEmployeeIds(value as number[])
            onSelectAllChange?.(false)
        }
    }

    useEffect(() => {
        if (form && formContext) {
            formContext.setValue(formName, employeeIds)
        }
    }, [employeeIds])

    const handleScroll = useCallback(
        (event: React.UIEvent<HTMLElement>) => {
            const target = event.currentTarget
            const nearBottom =
                target.scrollHeight - target.scrollTop - target.clientHeight <
                50
            if (nearBottom && hasNextPage && !isFetchingNextPage) {
                fetchNextPage()
            }
        },
        [fetchNextPage, hasNextPage, isFetchingNextPage],
    )

    const MenuPropsWithScroll = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
            onScroll: handleScroll,
        },
    }

    const renderValue = (selected: unknown) => {
        if (selectAll) return 'Todos'
        return data
            .filter(({ id }) => !checked?.includes(id))
            .filter(it =>
                (selected as number[]).some(select => select === it.id),
            )
            .map(({ nome }) => nome)
            .join(', ')
    }

    const searchField = (
        <Menu.Item
            key="employee-search"
            value=""
            onKeyDown={e => e.stopPropagation()}
            onClick={e => e.preventDefault()}
            sx={{ '&.Mui-focusVisible': { backgroundColor: 'transparent' } }}
        >
            <TextField
                size="small"
                placeholder="Buscar funcionário..."
                value={search}
                onChange={handleSearchChange}
                onClick={e => e.stopPropagation()}
                fullWidth
                autoFocus
                InputProps={{
                    endAdornment: isFetchingNextPage ? (
                        <InputAdornment position="end">
                            <CircularProgress size={16} />
                        </InputAdornment>
                    ) : undefined,
                }}
            />
        </Menu.Item>
    )

    const todosItem = (
        <Menu.Item key="employee-todos" value="todos">
            <Checkbox checked={selectAll} />
            <ListItemText primary="Todos" />
        </Menu.Item>
    )

    const employeeItems = data.map(({ id, nome }, index) => (
        <Menu.Item
            key={`employee-${index}`}
            value={id}
            disabled={checked?.includes(id)}
        >
            <Checkbox
                checked={
                    selectAll ||
                    checked?.includes(id) ||
                    employeeIds.some(it => it === id)
                }
            />
            <ListItemText primary={nome} />
        </Menu.Item>
    ))

    const loadingItem = isFetchingNextPage ? (
        <Menu.Item key="employee-loading" disabled>
            <Box display="flex" justifyContent="center" width="100%">
                <CircularProgress size={20} />
            </Box>
        </Menu.Item>
    ) : null

    return (
        <Box display="flex" alignItems="center" gap={1} width={width}>
            {form ? (
                <FormField
                    name={formName}
                    label="Funcionários"
                    size={size}
                    select
                    disabled={disabled}
                    helperText=" "
                    fullWidth
                    {...props}
                    SelectProps={{
                        multiple: true,
                        value: selectAll ? ['todos'] : employeeIds,
                        onChange: handleChange,
                        renderValue,
                        MenuProps: MenuPropsWithScroll,
                    }}
                >
                    {searchField}
                    {children}
                    {todosItem}
                    {employeeItems}
                    {loadingItem}
                </FormField>
            ) : (
                <TextField
                    label="Funcionários"
                    variant="filled"
                    SelectProps={{
                        multiple: true,
                        value: selectAll ? ['todos'] : employeeIds,
                        onChange: handleChange,
                        renderValue,
                        MenuProps: MenuPropsWithScroll,
                    }}
                    select
                    fullWidth
                    disabled={disabled}
                    size={size}
                    {...props}
                >
                    {searchField}
                    {children}
                    {data.length > 0 && todosItem}
                    {employeeItems}
                    {loadingItem}
                </TextField>
            )}
        </Box>
    )
}
